import { useCallback, useMemo, useSyncExternalStore } from "react";

const STORAGE_KEY = "arteria_talent_cart";
const SYNC_EVENT = "arteria:cart_updated";
const PROMPT_EVENT = "arteria:cart_prompt";

export interface TalentCartItem {
  id: string;
  first_name: string;
}

function readItems(): TalentCartItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    if (!Array.isArray(raw)) return [];
    // Back-compat: older carts stored a plain string[] of ids.
    return raw.map((entry: unknown) =>
      typeof entry === "string"
        ? { id: entry, first_name: "" }
        : { id: String((entry as TalentCartItem).id), first_name: (entry as TalentCartItem).first_name ?? "" },
    );
  } catch {
    return [];
  }
}

// Shared store: parsed once per change and fanned out to every subscriber,
// instead of every useTalentCart()/useIsInCart() instance independently
// re-parsing localStorage and re-rendering whenever ANY talent's cart
// membership changes (e.g. the header badge, every card in a scrolled grid).
let itemsCache: TalentCartItem[] = readItems();
const listeners = new Set<() => void>();
let installed = false;

function refresh() {
  itemsCache = readItems();
  listeners.forEach((l) => l());
}

function ensureInstalled() {
  if (installed) return;
  installed = true;
  // Same-tab updates (dispatched by persist() below)
  window.addEventListener(SYNC_EVENT, refresh);
  // Cross-tab updates: the native `storage` event only fires in OTHER tabs,
  // and only when localStorage actually changed under STORAGE_KEY.
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) refresh();
  });
}

function subscribe(listener: () => void) {
  ensureInstalled();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return itemsCache;
}

function persist(items: TalentCartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  // Deferred: dispatching synchronously would let this cascade into a setState
  // on unrelated mounted components (e.g. the header badge) while the caller's
  // own component is still mid-render, which React flags as a render-phase update.
  setTimeout(() => window.dispatchEvent(new Event(SYNC_EVENT)), 0);
}

/**
 * Plain function (not a hook) — safe to call from list items without
 * subscribing the calling component to the whole shared cart store.
 */
export function addTalent(talentId: string | number, fi?: string) {
  const id = String(talentId);
  const current = readItems();
  if (current.some((i) => i.id === id)) return;
  const next = [...current, { id, first_name: fi ?? "" }];
  persist(next);
  // Tells the global TalentCartPrompt modal to show itself for this talent
  window.dispatchEvent(
    new CustomEvent(PROMPT_EVENT, {
      detail: { name: fi || "This talent" },
    }),
  );
}

/** Plain function (not a hook) — safe to call without subscribing. */
export function removeTalent(talentId: string | number) {
  const id = String(talentId);
  const next = readItems().filter((i) => i.id !== id);
  persist(next);
}

/** Plain function (not a hook) — safe to call without subscribing. */
export function clearCart() {
  persist([]);
}

export function useTalentCart() {
  const items = useSyncExternalStore(subscribe, getSnapshot);

  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const isInCart = useCallback(
    (talentId: string | number) => items.some((i) => i.id === String(talentId)),
    [items],
  );

  const getTalentName = useCallback(
    (talentId: string | number) => items.find((i) => i.id === String(talentId))?.first_name || "",
    [items],
  );

  return {
    cartIds: ids,
    cartItems: items,
    cartCount: items.length,
    isInCart,
    getTalentName,
    addTalent,
    removeTalent,
    clearCart,
  };
}

/**
 * Lightweight, per-talent subscription: only re-renders the calling component
 * when THIS talent's cart membership actually flips, instead of on every cart
 * change anywhere on the page (unlike the broader `useTalentCart().isInCart(id)`).
 * Ideal for list/grid items (e.g. TalentCard) rendered in large numbers.
 */
export function useIsInCart(talentId: string | number): boolean {
  const id = String(talentId);
  return useSyncExternalStore(subscribe, () => itemsCache.some((i) => i.id === id));
}

export const TALENT_CART_PROMPT_EVENT = PROMPT_EVENT;
